import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useMangoApiClient } from '~/atoms/MangoApi'
import { UnreadVoicemailStatusFilter } from '~/types/ApiRequestTypes'

import type { ExtensionSelectResult } from '@mangovoice/mango-api-client'
import {
  ApiError,
  Extension,
  PaginatedApiResult,
  Voicemail,
  VoicemailInstance,
} from '@mangovoice/types'

export const VoicemailQueryKey = {
  VOICEMAILS: 'voicemails',
  VOICEMAIL: 'voicemail',
  EXTENSIONS: 'extensions',
  ALL_EXTENSIONS: 'all_extensions',
}

interface UseGetVoicemailsParams {
  pbxId: number
  extId?: string | number
  search?: string
  unread: UnreadVoicemailStatusFilter
  onRequestCompleted?: (data: PaginatedApiResult<Voicemail>) => void
  onRequestErrored?: (error: ApiError) => void
  options?: Omit<
    UseQueryOptions<PaginatedApiResult<Voicemail>, ApiError>,
    'queryKey'
  >
}

export const useGetVoicemails = ({
  pbxId,
  extId,
  search,
  unread,
  onRequestCompleted,
  options,
}: UseGetVoicemailsParams) => {
  const api = useMangoApiClient()
  const [params, setParams] = useState<{ offset: number }>()

  const query = useQuery<PaginatedApiResult<Voicemail>, ApiError>({
    queryKey: [
      VoicemailQueryKey.VOICEMAILS,
      { extId, search, unread, offset: params?.offset ?? 0 },
    ],
    queryFn: async () => {
      if (!pbxId) {
        return Promise.reject({} as ApiError)
      }

      const unreadParam =
        unread === UnreadVoicemailStatusFilter.ALL
          ? 'all'
          : unread === UnreadVoicemailStatusFilter.TRUE
            ? 'true'
            : 'false'

      const { ok, data } = await api.voicemails.getVoicemails({
        pbxId,
        extensionId: extId ? Number(extId) : undefined,
        search,
        offset: params?.offset,
        unread: unreadParam as 'all' | 'true' | 'false',
      })

      if (!ok) {
        return Promise.reject(data)
      }

      onRequestCompleted?.(data!)
      return data!
    },
    ...options,
    retry: options?.retry ?? false,
  })

  const refetch = (offset: number) => {
    setParams({ offset })
  }

  useEffect(() => {
    if (params?.offset === undefined) {
      return
    }

    query.refetch()
  }, [params])

  return {
    ...query,
    refetch,
  }
}

interface UseGetVoicemailParams {
  voicemailId: number
  onRequestCompleted?: (data: VoicemailInstance) => void
  onRequestErrored?: (error: ApiError) => void
  options?: Omit<
    UseQueryOptions<VoicemailInstance, ApiError | null>,
    'queryKey'
  >
}

export const useGetVoicemail = ({
  voicemailId,
  onRequestCompleted,
  onRequestErrored,
  options,
}: UseGetVoicemailParams) => {
  const api = useMangoApiClient()
  return useQuery<VoicemailInstance, ApiError | null>({
    queryKey: [VoicemailQueryKey.VOICEMAIL, { voicemailId }],
    queryFn: async () => {
      if (!voicemailId) {
        return Promise.reject(null)
      }
      const { ok, data, status } =
        await api.voicemails.getVoicemail(voicemailId)
      if (!ok) {
        const error = { status, ...(data as object) }
        onRequestErrored?.(error)
        return Promise.reject(error)
      }

      // Single cast: the retrieve endpoint schema is still declared with the
      // base Voicemail serializer, so generated types lack transcription/file_url.
      onRequestCompleted?.(data as VoicemailInstance)
      return data as VoicemailInstance
    },
    ...options,
    retry: options?.retry ?? false,
  })
}

// Refetch on the next view instead of patching the cached voicemail: writing
// read_at: null into an on-screen voicemail makes VoicemailPage mark it read again.
const markCachedVoicemailStale = (
  queryClient: QueryClient,
  voicemailId: number,
) =>
  queryClient.invalidateQueries({
    queryKey: [VoicemailQueryKey.VOICEMAIL, { voicemailId }],
    refetchType: 'none',
  })

interface UseDeleteVoicemailParams {
  onRequestCompleted?: ({
    data,
    voicemailId,
  }: {
    data: { message: string }
    voicemailId: number
  }) => void
}

export const useDeleteVoicemail = ({
  onRequestCompleted,
}: UseDeleteVoicemailParams) => {
  const api = useMangoApiClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (voicemailId: number) =>
      api.voicemails.deleteVoicemail(voicemailId),
    onSuccess: (result, variables) => {
      markCachedVoicemailStale(queryClient, variables)
      onRequestCompleted?.({
        data: result.data as { message: string },
        voicemailId: variables,
      })
    },
  })
}

interface UseMarkVoicemailReadParams {
  onRequestCompleted?: ({
    data,
    voicemailId,
  }: {
    data: { message: string }
    voicemailId: number
  }) => void
}
export const useMarkVoicemailRead = ({
  onRequestCompleted,
}: UseMarkVoicemailReadParams) => {
  const api = useMangoApiClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (voicemailId: number) =>
      api.voicemails.markVoicemailRead(voicemailId),
    onSuccess: (response, variables) => {
      markCachedVoicemailStale(queryClient, variables)
      onRequestCompleted?.({
        data: response.data as { message: string },
        voicemailId: variables,
      })
    },
    retry: false,
  })
}

interface UseMarkVoicemailUnreadParams {
  onRequestCompleted?: ({
    data,
    voicemailId,
  }: {
    data: { message: string }
    voicemailId: number
  }) => void
}
export const useMarkVoicemailUnread = ({
  onRequestCompleted,
}: UseMarkVoicemailUnreadParams) => {
  const api = useMangoApiClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (voicemailId: number) =>
      api.voicemails.markVoicemailUnread(voicemailId),
    onSuccess: (response, variables) => {
      markCachedVoicemailStale(queryClient, variables)
      onRequestCompleted?.({
        data: response.data as { message: string },
        voicemailId: variables,
      })
    },
    retry: false,
  })
}

export const useMoveVoicemail = () => {
  const api = useMangoApiClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      voicemailId,
      extensionId,
    }: {
      voicemailId: number
      extensionId: number
    }) => api.voicemails.moveVoicemail(voicemailId, extensionId),
    onSuccess: (_, { voicemailId }) => {
      markCachedVoicemailStale(queryClient, voicemailId)
    },
    onError: (error) => {
      console.error('Error moving voicemail:', error)
    },
    retry: false,
  })
}

interface UseGetVoicemailExtensionsParams {
  pbxId: number
  onRequestCompleted?: (data: PaginatedApiResult<Extension>) => void
  onRequestErrored?: (error: ApiError) => void
  options?: Omit<
    UseQueryOptions<PaginatedApiResult<Extension>, ApiError>,
    'queryKey'
  >
}

export const useGetVoicemailExtensions = ({
  pbxId,
  onRequestCompleted,
  onRequestErrored,
  options = {},
}: UseGetVoicemailExtensionsParams) => {
  const api = useMangoApiClient()
  return useQuery<PaginatedApiResult<Extension>, ApiError>({
    queryKey: [VoicemailQueryKey.EXTENSIONS, pbxId],
    queryFn: async () => {
      if (pbxId === 0) {
        return Promise.reject({} as ApiError)
      }

      const { ok, data, status } = await api.extensions.listLegacy({
        pbx_id: pbxId,
      })
      if (!ok || !data) {
        const error = { status, ...(data as object) }
        onRequestErrored?.(error)
        return Promise.reject(error)
      }

      onRequestCompleted?.(data)
      return data
    },
    ...options,
    retry: options.retry ?? false,
  })
}

interface UseGetAllExtensionsParams {
  pbxId: number
  onRequestCompleted?: (data: PaginatedApiResult<ExtensionSelectResult>) => void
  onRequestErrored?: (error: ApiError) => void
  options?: Omit<
    UseQueryOptions<PaginatedApiResult<ExtensionSelectResult>, ApiError>,
    'queryKey'
  >
}

export const useGetAllExtensions = ({
  pbxId,
  onRequestCompleted,
  onRequestErrored,
  options = {},
}: UseGetAllExtensionsParams) => {
  const api = useMangoApiClient()
  return useQuery<PaginatedApiResult<ExtensionSelectResult>, ApiError>({
    queryKey: [VoicemailQueryKey.ALL_EXTENSIONS, pbxId],
    queryFn: async () => {
      if (!pbxId) {
        return Promise.reject({} as ApiError)
      }

      const { ok, data, status } = await api.extensions.getAllExtensions({
        pbx_id: pbxId,
        include_voicemail_status: true,
        limit: 1000,
      })
      if (!ok || !data) {
        const error = { status, ...(data as object) }
        onRequestErrored?.(error)
        return Promise.reject(error)
      }

      onRequestCompleted?.(data)
      return data
    },
    ...options,
    retry: options.retry ?? false,
  })
}
